import React, { useCallback } from 'react';
import { useNavigate, useLocation, useHref } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-aria-components';
import AutoScroll from '../components/utilities/AutoScroll/AutoScroll.js';

function AppRouterProvider({ config, children }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();

  // Router base path not supported in React Spectrum so need to strip during navigate.
  // https://github.com/adobe/react-spectrum/pull/5395#issuecomment-1805056540
  const routerNavigate = useCallback(
    (href, options) => {
      const basePath = config.basePath[i18n.language];
      const basePathTranslated = config.basePath[i18n.language === 'en' ? 'cy' : 'en'];

      // Redirect to correct path for language switching in development mode only.
      if (config.nodeEnv === 'development') {
        if (basePath && typeof href === 'string' && href.startsWith(basePathTranslated)) {
          return (window.location.href = href);
        }
      }

      if (basePath && typeof href === 'string' && href.startsWith(basePath)) {
        href = href.slice(basePath.length);
      }

      if (basePath && typeof href === 'string' && href.startsWith(basePathTranslated)) {
        href = href.slice(basePathTranslated.length);
      }

      // If navigating to a same page ID then perform the scroll.
      // https://dev.azure.com/ccdg/CWO%20-%20Events/_workitems/edit/13250
      const hash = href.split('#')[1];
      if (hash) {
        setTimeout(() => {
          const id = hash.replace('#', '');
          const element = document.getElementById(id);

          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500);
      }

      return navigate(href, options);
    },
    [navigate]
  );

  return (
    <RouterProvider navigate={routerNavigate} useHref={useHref}>
      <AutoScroll pathname={pathname} hash={hash} />
      {children}
    </RouterProvider>
  );
}

export default AppRouterProvider;
